import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getTimeRange, kphToMph, parseUTC } from '../utils/utils';
import { useInterval } from './useInterval';
import { supabase } from '../supabaseClient';

const fetchHistoricalData = async (site, startDate, endDate) => {
  let windData = JSON.parse(localStorage.getItem(`${site}_lastWindData`) || '[]');
  let pmData = JSON.parse(localStorage.getItem(`${site}_lastPmData`) || '[]');

  try {
    const [
      { data: windRes, error: windError },
      { data: pmRes, error: pmError },
    ] = await Promise.all([
      supabase
        .from('ivanhoe_weather_met')
        .select('time_utc, wind_speed_kph, wind_gust_kph, wind_direction')
        .eq('site', site)
        .gte('time_utc', startDate)
        .lte('time_utc', endDate)
        .order('time_utc', { ascending: true })
        .limit(5000),
      supabase
        .from('ivanhoe_weather_pm')
        .select('time_utc, pm2_5_ugm3, pm10_ugm3')
        .eq('site', site)
        .gte('time_utc', startDate)
        .lte('time_utc', endDate)
        .order('time_utc', { ascending: true })
        .limit(5000),
    ]);

    if (windError) {
      throw windError;
    }

    if (pmError) {
      throw pmError;
    }

    windData = Array.isArray(windRes) ? windRes : [];
    pmData = Array.isArray(pmRes) ? pmRes : [];

    localStorage.setItem(`${site}_lastWindData`, JSON.stringify(windData));
    localStorage.setItem(`${site}_lastPmData`, JSON.stringify(pmData));
  } catch (err) {
    console.error(`Error fetching data for ${site}:`, err.message);
  }

  return { windData, pmData };
};

export const useHistoricalData = (site, dateRange, refreshInterval = 0) => {
  const [historicalWindData, setHistoricalWindData] = useState([]);
  const [historicalPMData, setHistoricalPMData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!site) {
      setError('Site is required');
      setLoading(false);
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    const range = getTimeRange(dateRange); // Always use the provided dateRange
    const startDate = new Date(range.start).toISOString();
    const endDate = new Date(range.end).toISOString();

    try {
      const { windData, pmData } = await fetchHistoricalData(site, startDate, endDate);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setHistoricalWindData(windData);
      setHistoricalPMData(pmData);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setError(`Failed to fetch data: ${err.message}. Using cached data if available.`);
      console.error('Error fetching historical data:', err);
      setHistoricalWindData(JSON.parse(localStorage.getItem(`${site}_lastWindData`) || '[]'));
      setHistoricalPMData(JSON.parse(localStorage.getItem(`${site}_lastPmData`) || '[]'));
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [site, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshInterval]);

  useInterval(() => {
    if (refreshInterval > 0) {
      fetchData();
    }
  }, refreshInterval > 0 ? refreshInterval * 60 * 1000 : null);

  const combinedData = useMemo(() => {
    const allDataMap = new Map();

    historicalWindData.forEach((wind) => {
      if (!wind || !wind.time_utc) return;
      const timeUtc = wind.time_utc.endsWith('Z') ? wind.time_utc : `${wind.time_utc}Z`;
      const timestamp = parseUTC(timeUtc);
      if (timestamp !== null) {
        allDataMap.set(timeUtc, {
          time_utc: timestamp,
          wind_speed_mph: wind.wind_speed_kph != null ? kphToMph(wind.wind_speed_kph) : null,
          wind_gust_mph: wind.wind_gust_kph != null ? kphToMph(wind.wind_gust_kph) : null,
          pm2_5_ugm3: null,
          pm10_ugm3: null,
        });
      }
    });

    historicalPMData.forEach((pm) => {
      if (!pm || !pm.time_utc) return;
      const timeUtc = pm.time_utc.endsWith('Z') ? pm.time_utc : `${pm.time_utc}Z`;
      const timestamp = parseUTC(timeUtc);
      if (timestamp !== null) {
        if (allDataMap.has(timeUtc)) {
          const existing = allDataMap.get(timeUtc);
          allDataMap.set(timeUtc, {
            ...existing,
            pm2_5_ugm3: pm.pm2_5_ugm3,
            pm10_ugm3: pm.pm10_ugm3,
          });
        } else {
          allDataMap.set(timeUtc, {
            time_utc: timestamp,
            wind_speed_mph: null,
            wind_gust_mph: null,
            pm2_5_ugm3: pm.pm2_5_ugm3,
            pm10_ugm3: pm.pm10_ugm3,
          });
        }
      }
    });

    return Array.from(allDataMap.values()).sort((a, b) => a.time_utc - b.time_utc);
  }, [historicalWindData, historicalPMData]);

  return { combinedData, loading, error, fetchData };
};
